import React, { FC, useContext, useState } from "react";
import { defineMessages, useIntl } from "react-intl";
import Svg from "@natera/material/lib/svg";
import HelpIcon from "@assets/svg/icons/help.svg";
import { InfoCard, InfoCardField, SpinnerView } from "@app/components";
import { ProfileContext } from "@app/provider/profile";
import { routes } from "@app/routing";
import { BlueHeaderContent } from "@app/components/ui/layout";
import DobHelpDialog from "./dobHelpDialog";

import "./personalInformationPage.scss";

const messages = defineMessages({
  personalInformationPageBackButton: {
    id: "personalInformationPageBackButton",
    defaultMessage: "Personal Information",
  },
  personalInformationPagePrimaryTitle: {
    id: "personalInformationPagePrimaryTitle",
    defaultMessage: "Personal Information",
  },
  personalInformationPageFirstName: {
    id: "personalInformationPageFirstName",
    defaultMessage: "First Name",
  },
  personalInformationPageMiddleName: {
    id: "personalInformationPageMiddleName",
    defaultMessage: "Middle Name / Initial",
  },
  personalInformationPageLastName: {
    id: "personalInformationPageLastName",
    defaultMessage: "Last Name",
  },
  personalInformationPageAlternativeLastName: {
    id: "personalInformationPageAlternativeLastName",
    defaultMessage: "Alternate Last Name",
  },
  personalInformationPageDateOfBirth: {
    id: "personalInformationPageDateOfBirth",
    defaultMessage: "Date of Birth",
  },
  personalInformationPageDateOfBirthHelpAriaLabel: {
    id: "personalInformationPageDateOfBirthHelpAriaLabel",
    defaultMessage: "More information about editing your date of birth",
  },
});

const PersonalInformationPage: FC = () => {
  const intl = useIntl();
  const [dobDialogOpen, setDobDialogOpen] = useState(false);

  const { profileData: patientProfileData, profileDataIsLoading } =
    useContext(ProfileContext);

  return (
    <BlueHeaderContent
      blueHeaderTitle={intl.formatMessage(
        messages.personalInformationPageBackButton
      )}
      backArrowHeaderRoute={routes.profile}
    >
      {!profileDataIsLoading && patientProfileData && (
        <>
          <InfoCard
            titleText={intl.formatMessage(
              messages.personalInformationPagePrimaryTitle
            )}
            titleEditButtonLinkRoute={routes.updatePersonalInformation}
          >
            <InfoCardField
              label={intl.formatMessage(
                messages.personalInformationPageFirstName
              )}
              value={patientProfileData?.firstName}
            />
            {patientProfileData?.middleName &&
              patientProfileData?.middleName.length > 0 && (
                <InfoCardField
                  label={intl.formatMessage(
                    messages.personalInformationPageMiddleName
                  )}
                  value={patientProfileData?.middleName}
                />
              )}
            <InfoCardField
              label={intl.formatMessage(
                messages.personalInformationPageLastName
              )}
              value={patientProfileData?.lastName}
            />
            {patientProfileData?.alternativeLastName &&
              patientProfileData?.alternativeLastName.length > 0 && (
                <InfoCardField
                  label={intl.formatMessage(
                    messages.personalInformationPageAlternativeLastName
                  )}
                  value={patientProfileData?.alternativeLastName}
                />
              )}
          </InfoCard>
          <InfoCard>
            <InfoCardField
              label={
                <span className="personal-information-page__dob-label">
                  {intl.formatMessage(
                    messages.personalInformationPageDateOfBirth
                  )}
                  <button
                    type="button"
                    className="personal-information-page__dob-help-trigger"
                    aria-label={intl.formatMessage(
                      messages.personalInformationPageDateOfBirthHelpAriaLabel
                    )}
                    aria-haspopup="dialog"
                    onClick={() => setDobDialogOpen(true)}
                  >
                    <Svg
                      content={HelpIcon}
                      aria-hidden
                      className="personal-information-page__dob-help-icon"
                    />
                  </button>
                </span>
              }
              value={patientProfileData?.dateOfBirth}
            />
          </InfoCard>
        </>
      )}
      {dobDialogOpen && (
        <DobHelpDialog onClose={() => setDobDialogOpen(false)} />
      )}
      <SpinnerView isLoading={profileDataIsLoading} />
    </BlueHeaderContent>
  );
};

export default PersonalInformationPage;
